import { APIConfig } from "@/lib/api";
import { ApiError } from "@/lib/api/api-error";
import { request as __request } from "@/lib/api/request";
import { type SseFrame, decodeSse } from "@/lib/api/sse";
import { handleSessionExpired } from "@/lib/session-expiration";
import { getCsrfToken } from "@/providers/auth";
import {
  AgentStreamConnectionError,
  AgentStreamProtocolError,
} from "./agent-stream-errors";
import {
  type AgentStreamFrame,
  type ApprovalDecisionPayload,
  type CreateAgentSessionPayload,
  agentEventStreamDataSchema,
  agentSessionSchema,
  agentSessionsSchema,
  approvalDecisionPayloadSchema,
  approvalRecordSchema,
  availableAgentsSchema,
  completeStreamDataSchema,
  createAgentSessionPayloadSchema,
  errorStreamDataSchema,
  messageDeltaStreamDataSchema,
  messageResponseSchema,
} from "./types/agent-gateway";

const AGENT_MESSAGE_ERRORS = {
  400: "Invalid message",
  401: "Unauthorized",
  403: "Agent access denied",
  404: "Agent session not found",
  409: "Message request conflict",
  429: "Message rate limit exceeded",
  500: "Internal server error",
  502: "Agent upstream failure",
  503: "Agent service unavailable",
  504: "Agent request timed out",
} as const;

const AGENT_APPROVAL_ERRORS = {
  400: "Invalid approval decision",
  401: "Unauthorized",
  403: "Approval access denied",
  404: "Approval request not found",
  409: "Approval request conflict",
  429: "Approval rate limit exceeded",
  500: "Internal server error",
  502: "Agent upstream failure",
  503: "Agent service unavailable",
  504: "Agent request timed out",
} as const;

const AGENT_END_SESSION_ERRORS = {
  400: "Invalid end-session request",
  401: "Unauthorized",
  403: "Session access denied",
  404: "Agent session not found",
  409: "End-session request conflict",
  429: "Session rate limit exceeded",
  500: "Internal server error",
  502: "Agent upstream failure",
  503: "Agent service unavailable",
  504: "Agent request timed out",
} as const;

function parseStreamFrame(frame: SseFrame): AgentStreamFrame {
  let data: unknown;

  try {
    data = JSON.parse(frame.data);
  } catch {
    throw new AgentStreamProtocolError();
  }

  try {
    switch (frame.event) {
      case "message_delta":
        return {
          event: frame.event,
          id: frame.id,
          data: messageDeltaStreamDataSchema.parse(data),
        };
      case "agent_event":
        return {
          event: frame.event,
          id: frame.id,
          data: agentEventStreamDataSchema.parse(data),
        };
      case "complete":
        return {
          event: frame.event,
          id: frame.id,
          data: completeStreamDataSchema.parse(data),
        };
      case "error":
        return {
          event: frame.event,
          id: frame.id,
          data: errorStreamDataSchema.parse(data),
        };
      default:
        throw new AgentStreamProtocolError();
    }
  } catch (error) {
    if (error instanceof AgentStreamProtocolError) {
      throw error;
    }
    throw new AgentStreamProtocolError();
  }
}

async function getErrorBody(response: Response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function* streamAgentRun({
  agentKey,
  sessionId,
  endpoint,
  body,
  idempotencyKey,
  signal,
}: {
  agentKey: string;
  sessionId: string;
  endpoint: "messages/stream/" | "start/stream/";
  body: Record<string, unknown>;
  idempotencyKey: string;
  signal: AbortSignal;
}): AsyncGenerator<AgentStreamFrame> {
  const encodedAgentKey = encodeURIComponent(agentKey);
  const encodedSessionId = encodeURIComponent(sessionId);
  const path =
    `/api/v1/agents/${encodedAgentKey}/sessions/${encodedSessionId}/${endpoint}`;
  const url = `${APIConfig.BASE}${path}`;
  const csrfToken = getCsrfToken();
  const requestOptions = {
    method: "POST" as const,
    url: path,
    headers: { "Idempotency-Key": idempotencyKey },
    mediaType: "application/json",
    body,
    errors: AGENT_MESSAGE_ERRORS,
  };

  let response: Response;

  try {
    response = await fetch(url, {
      method: "POST",
      credentials: APIConfig.CREDENTIALS,
      signal,
      headers: {
        Accept: "text/event-stream",
        "Content-Type": "application/json",
        "Idempotency-Key": idempotencyKey,
        ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch (error) {
    if (signal.aborted) {
      throw error;
    }

    throw new AgentStreamConnectionError();
  }

  if (!response.ok) {
    const errorBody = await getErrorBody(response);
    if (response.status === 401) {
      handleSessionExpired();
    }

    throw new ApiError(
      requestOptions,
      {
        body: errorBody,
        ok: false,
        status: response.status,
        statusText: response.statusText,
        url,
      },
      AGENT_MESSAGE_ERRORS[
        response.status as keyof typeof AGENT_MESSAGE_ERRORS
      ] ?? "Agent stream request failed",
    );
  }

  const contentType = response.headers.get("content-type") ?? "";
  const mediaType = contentType.split(";", 1)[0].trim().toLowerCase();
  if (mediaType !== "text/event-stream") {
    throw new AgentStreamProtocolError();
  }

  if (!response.body) {
    throw new AgentStreamProtocolError();
  }

  try {
    for await (const rawFrame of decodeSse(response.body)) {
      const frame = parseStreamFrame(rawFrame);
      if (frame.data.session_id !== sessionId) {
        throw new AgentStreamProtocolError();
      }
      yield frame;

      if (frame.event === "complete" || frame.event === "error") {
        return;
      }
    }
  } catch (error) {
    if (signal.aborted || error instanceof AgentStreamProtocolError) {
      throw error;
    }

    throw new AgentStreamConnectionError();
  }

  throw new AgentStreamConnectionError();
}

export class AgentGatewayService {
  static listAvailableAgents() {
    return __request(
      APIConfig,
      {
        method: "GET",
        url: "/api/v1/agents/",
      },
      availableAgentsSchema,
    );
  }

  static listSessions(agentKey: string) {
    return __request(
      APIConfig,
      {
        method: "GET",
        url: "/api/v1/agents/{agent_key}/sessions/",
        path: { agent_key: agentKey },
      },
      agentSessionsSchema,
    );
  }

  static createSession(
    agentKey: string,
    payload: CreateAgentSessionPayload = {},
  ) {
    const body = createAgentSessionPayloadSchema.parse(payload);

    return __request(
      APIConfig,
      {
        method: "POST",
        url: "/api/v1/agents/{agent_key}/sessions/",
        path: { agent_key: agentKey },
        mediaType: "application/json",
        body,
      },
      agentSessionSchema,
    );
  }

  static getSession(agentKey: string, sessionId: string) {
    return __request(
      APIConfig,
      {
        method: "GET",
        url: "/api/v1/agents/{agent_key}/sessions/{session_id}/",
        path: {
          agent_key: agentKey,
          session_id: sessionId,
        },
      },
      agentSessionSchema,
    );
  }

  static endSession(
    agentKey: string,
    sessionId: string,
    idempotencyKey: string,
  ) {
    return __request(
      APIConfig,
      {
        method: "POST",
        url: "/api/v1/agents/{agent_key}/sessions/{session_id}/end/",
        path: {
          agent_key: agentKey,
          session_id: sessionId,
        },
        headers: {
          "Idempotency-Key": idempotencyKey,
        },
        mediaType: "application/json",
        body: {},
        errors: AGENT_END_SESSION_ERRORS,
      },
      agentSessionSchema,
    );
  }

  static sendMessage(
    agentKey: string,
    sessionId: string,
    text: string,
    idempotencyKey: string,
  ) {
    return __request(
      APIConfig,
      {
        method: "POST",
        url: "/api/v1/agents/{agent_key}/sessions/{session_id}/messages/",
        path: {
          agent_key: agentKey,
          session_id: sessionId,
        },
        headers: {
          "Idempotency-Key": idempotencyKey,
        },
        mediaType: "application/json",
        body: { text },
        errors: AGENT_MESSAGE_ERRORS,
      },
      messageResponseSchema,
    );
  }

  static respondToInput(
    agentKey: string,
    sessionId: string,
    interruptId: string,
    response: unknown,
    idempotencyKey: string,
  ) {
    return __request(
      APIConfig,
      {
        method: "POST",
        url: "/api/v1/agents/{agent_key}/sessions/{session_id}/inputs/{interrupt_id}/",
        path: {
          agent_key: agentKey,
          session_id: sessionId,
          interrupt_id: interruptId,
        },
        headers: {
          "Idempotency-Key": idempotencyKey,
        },
        mediaType: "application/json",
        body: { response },
        errors: AGENT_MESSAGE_ERRORS,
      },
      messageResponseSchema,
    );
  }

  static async *streamMessage(
    agentKey: string,
    sessionId: string,
    text: string,
    idempotencyKey: string,
    signal: AbortSignal,
  ): AsyncGenerator<AgentStreamFrame> {
    yield* streamAgentRun({
      agentKey,
      sessionId,
      endpoint: "messages/stream/",
      body: { text },
      idempotencyKey,
      signal,
    });
  }

  static async *streamWorkflowStart(
    agentKey: string,
    sessionId: string,
    idempotencyKey: string,
    signal: AbortSignal,
  ): AsyncGenerator<AgentStreamFrame> {
    yield* streamAgentRun({
      agentKey,
      sessionId,
      endpoint: "start/stream/",
      body: {},
      idempotencyKey,
      signal,
    });
  }

  static decideApproval(
    agentKey: string,
    sessionId: string,
    payload: ApprovalDecisionPayload,
    idempotencyKey: string,
  ) {
    const body = approvalDecisionPayloadSchema.parse(payload);

    return __request(
      APIConfig,
      {
        method: "POST",
        url: "/api/v1/agents/{agent_key}/sessions/{session_id}/approvals/",
        path: {
          agent_key: agentKey,
          session_id: sessionId,
        },
        headers: {
          "Idempotency-Key": idempotencyKey,
        },
        mediaType: "application/json",
        body,
        errors: AGENT_APPROVAL_ERRORS,
      },
      approvalRecordSchema,
    );
  }

  static retryApprovalContinuation(
    agentKey: string,
    sessionId: string,
    approvalId: string,
    idempotencyKey: string,
  ) {
    return __request(
      APIConfig,
      {
        method: "POST",
        url: "/api/v1/agents/{agent_key}/sessions/{session_id}/approvals/{approval_id}/resume/",
        path: {
          agent_key: agentKey,
          session_id: sessionId,
          approval_id: approvalId,
        },
        headers: {
          "Idempotency-Key": idempotencyKey,
        },
        mediaType: "application/json",
        body: {},
        errors: AGENT_APPROVAL_ERRORS,
      },
      messageResponseSchema,
    );
  }
}
